
import dayjs from 'dayjs'

import { fetchRunsheetOrdersByShift } from '@/entities/orders/thunks/fetchRunsheetOrdersByShift'
import { fetchUnassignedOrdersByRunsheet } from '@/entities/orders/thunks/fetchUnassignedOrdersByRunsheet'
import { selectAllRunsheetsWithShifts } from '@/entities/schedules'
import { widgetWindowsSelector } from '@/entities/widgetWindows'
import { OperationsMapWidgetWindow } from '@/entities/widgetWindows/models/WidgetWindow'
import { updateWidgetWindow } from '@/entities/widgetWindows/widgetWindowsSlice'
import { MosaicAdditionalToolbar } from '@/shared/components'
import { DropdownOption } from '@/shared/components/FilterMultiselectDropdown/FilterMultiselectDropdown'
import { MosaicToolbarMenuItem } from '@/shared/components/MosaicAdditionalToolbar/models/MosaicToolbarMenuItem'
import { useAppDispatch, useAppSelector } from '@/shared/store'
import { faHistory, faLocationDot, faTrash } from '@fortawesome/pro-regular-svg-icons'

import { useReplayContext } from './HistoricalReplay/ReplayContext'
import { useOperationMapContext } from './OperationsMapWidgetContext'

export type RunsheetOperationsMapWidgetToolbarProps = {
  //
}

// TODO: Add an API endpoint to retrieve the list of shifts for the dropdown filter
export const RunsheetOperationsMapWidgetToolbar: React.FC<RunsheetOperationsMapWidgetToolbarProps> = () => {
  const { widgetId } = useOperationMapContext()
  const { isReplayPanelVisible, setIsReplayPanelVisible } = useReplayContext()
  const mapWidget = useAppSelector((state) => widgetWindowsSelector.selectById(state, widgetId))
  const selectedShiftIds = (mapWidget as OperationsMapWidgetWindow)?.shiftIds || []
  const selectedRunsheetIds = (mapWidget as OperationsMapWidgetWindow)?.unassignedScheduleIds || []
  const selectedLiveLocationShiftIds = (mapWidget as OperationsMapWidgetWindow)?.liveLocationShiftIds || []
  const runsheetsWithShifts = useAppSelector((state) => selectAllRunsheetsWithShifts(state))
  const dispatch = useAppDispatch()

  // TODO: Create an API endpoint to retrieve the list of runsheets and shifts for the dropdown filter
  const runsheetShiftOptions: DropdownOption[] = runsheetsWithShifts.flatMap((runsheet) =>
    runsheet.shifts.map((shift) => ({ label: `${runsheet.name} > ${shift.name}`, value: shift.id })),
  )

  const selectedShiftOptions = selectedShiftIds
    .map((shiftId) => runsheetShiftOptions.find((option) => option.value === shiftId))
    .filter(option => option !== undefined)

  const runsheetRunsheetOptions: DropdownOption[] = runsheetsWithShifts.map((runsheet) => ({
    label: runsheet.name,
    value: runsheet.id,
  }))

  const selectedRunsheetOptions = selectedRunsheetIds
    .map((runsheetId) => runsheetRunsheetOptions.find((option) => option.value === runsheetId))
    .filter(option => option !== undefined)

  const liveShiftOptions: DropdownOption[] = runsheetsWithShifts
    .filter(runsheet => runsheet.dateOfOperation === dayjs().format('DD/MM/YYYY'))
    .flatMap((runsheet) =>
      runsheet.shifts.map((shift) => ({ label: `${runsheet.name} > ${shift.name}`, value: shift.id })),
    )

  const selectedLiveLocationOptions: DropdownOption[] = selectedLiveLocationShiftIds
    .map((shiftId) => liveShiftOptions.find((option) => option.value === shiftId))
    .filter(option => option !== undefined) as DropdownOption[]

  // Select shift routes for display
  const handleSelectShift = (val: DropdownOption) => {
    const index = selectedShiftIds.findIndex(id => id === val.value)

    if (index > -1) {
      const newSelectedShifts = [...selectedShiftIds]

      newSelectedShifts.splice(index, 1)

      dispatch(updateWidgetWindow({
        id: widgetId,
        changes: {
          shiftIds: newSelectedShifts,
        },
      }))
    } else {
      const newSelectedShiftIds = [...selectedShiftIds, val.value]

      dispatch(fetchRunsheetOrdersByShift({ shiftId: val.value }))
      dispatch(updateWidgetWindow({
        id: widgetId,
        changes: {
          shiftIds: newSelectedShiftIds,
        },
      }))
    }
  }

  const handleClearAllShifts = () => {
    dispatch(updateWidgetWindow({
      id: widgetId,
      changes: {
        shiftIds: [],
      },
    }))
  }

  // Select unassigned orders for display
  const handleSelectUnassignedRunsheet = (val: DropdownOption) => {
    const index = selectedRunsheetIds.findIndex(id => id === val.value)

    if (index > -1) {
      const newSelectedRunsheetIds = [...selectedRunsheetIds]

      newSelectedRunsheetIds.splice(index, 1)

      dispatch(updateWidgetWindow({
        id: widgetId,
        changes: {
          unassignedScheduleIds: newSelectedRunsheetIds,
        },
      }))
    } else {
      const newSelectedRunsheetIds = [...selectedRunsheetIds, val.value]

      dispatch(fetchUnassignedOrdersByRunsheet({ runsheetId: val.value }))
      dispatch(updateWidgetWindow({
        id: widgetId,
        changes: {
          unassignedScheduleIds: newSelectedRunsheetIds,
        },
      }))
    }
  }

  const handleClearUnassignedRunsheets = () => {
    dispatch(updateWidgetWindow({
      id: widgetId,
      changes: {
        unassignedScheduleIds: [],
      },
    }))
  }

  // Select vehicle shift to show live location
  const handleSelectLiveLocationShift = (val: DropdownOption) => {
    const index = selectedLiveLocationShiftIds.findIndex(id => id === val.value)

    if (index > -1) {
      const newSelectedLiveLocationIds = [...selectedLiveLocationShiftIds]

      newSelectedLiveLocationIds.splice(index, 1)

      dispatch(updateWidgetWindow({
        id: widgetId,
        changes: {
          liveLocationShiftIds: newSelectedLiveLocationIds,
        },
      }))
    } else {
      const newSelectedLiveLocationIds = [...selectedLiveLocationShiftIds, val.value]

      dispatch(fetchUnassignedOrdersByRunsheet({ runsheetId: val.value }))
      dispatch(updateWidgetWindow({
        id: widgetId,
        changes: {
          liveLocationShiftIds: newSelectedLiveLocationIds,
        },
      }))
    }
  }

  const handleClearLiveLocationShift = () => {
    dispatch(updateWidgetWindow({
      id: widgetId,
      changes: {
        liveLocationShiftIds: [],
      },
    }))
  }

  const toolbarMenuItems: MosaicToolbarMenuItem[] = [
    {
      type: 'multidropdown',
      label: 'Vehicle Shifts',
      values: (selectedShiftOptions as DropdownOption[]),
      options: runsheetShiftOptions,
      searchActionEnable: true,
      searchActionIcon: faTrash,
      onSelectItem: handleSelectShift,
      slotProps: {
        search: {
          placeholder: 'Search runsheet shifts',
        },
        searchAction: {
          onClick: handleClearAllShifts,
          tooltipTitle: 'Clear selected shifts',
          disabled: selectedShiftIds.length === 0,
        },
      },
    },
    {
      type: 'multidropdown',
      label: 'Unassigned Orders',
      values: (selectedRunsheetOptions as DropdownOption[]),
      options: runsheetRunsheetOptions,
      searchActionEnable: true,
      searchActionIcon: faTrash,
      onSelectItem: handleSelectUnassignedRunsheet,
      slotProps: {
        search: {
          placeholder: 'Search runsheet',
        },
        searchAction: {
          onClick: handleClearUnassignedRunsheets,
          tooltipTitle: 'Clear selected runsheets',
          disabled: selectedRunsheetIds.length === 0,
        },
      },
    },
    {
      type: 'actionItem',
      label: 'Historical Trace',
      icon: faHistory,
      onClick: () => setIsReplayPanelVisible(!isReplayPanelVisible),
      float: 'right',
    },
    {
      type: 'multidropdown',
      icon: faLocationDot,
      label: 'Live Locations',
      float: 'right',
      values: (selectedLiveLocationOptions as DropdownOption[]),
      options: liveShiftOptions,
      searchActionEnable: true,
      searchActionIcon: faTrash,
      onSelectItem: handleSelectLiveLocationShift,
      slotProps: {
        search: {
          placeholder: 'Search shifts',
        },
        searchAction: {
          onClick: handleClearLiveLocationShift,
          tooltipTitle: 'Clear selected shifts',
          disabled: selectedLiveLocationOptions.length === 0,
        },
      },
    },
  ]

  return (
    <MosaicAdditionalToolbar
      toolbarMenuItems={toolbarMenuItems}
      averageMenuItemWidth={120}
    />
  )
}
